// React Dependencies
import React, { useState, forwardRef, useEffect } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { Skeleton } from '@material-ui/lab';

// Internal Dependencies
import colours from '../../styles/colours';
import { IsEmpty } from '../../common/checks';

const ProgressiveImage = forwardRef((props, ref) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [comImage, setComImage] = useState(null);

  const hasImage = !IsEmpty(comImage);
  const squareSkeleton = !hasImage && (!props.height || props.height === 'auto');

  useEffect(() => {
    // To avoid UI stucked at showing skeleton on UI refresh,
    // we need to check if there is an actual change in the image
    // before we show skeleton for new image to load.
    if (IsEmpty(props.image) || props.image !== comImage) {
      setIsLoaded(false);
      setComImage(props.image);
    }
  }, [props.image]);

  const imgProps = {
    ref,
    alt:       props.alt,
    draggable: props.draggable,
    src:       comImage,
    onClick:   props.onClick,
    onError:   () => setIsLoaded(false),
    style:     { width: props.width, height: props.height, backgroundColor: 'transparent', ...props.style },
    onLoad:    (e) => {
      setIsLoaded(true);
      props.onLoad(e);
    },
  };

  return (
    <div
      style={{
        width:    props.width,
        height:   props.height,
        position: 'relative',
        ...props.style,
        ...(squareSkeleton ? { aspectRatio: '1 / 1' } : {}),
      }}
    >
      {
        hasImage && <img alt="" {...imgProps} />
      }
      <div style={{
        width:         props.width,
        // Height cannot be 'auto' else skeleton doesn't show up
        height:        (props.height && props.height !== 'auto') ? props.height : '100%',
        zIndex:        (props.style && props.style.zIndex) ? props.style.zIndex + 1 : 5,
        position:      'absolute',
        top:           0,
        left:          0,
        right:         0,
        pointerEvents: 'none',
      }}
      >
        {
          !isLoaded
            && (props.LoadingComponent
              ? <props.LoadingComponent />
              : (
                <Skeleton
                  animation="wave"
                  variant="rect"
                  width={props.width}
                  // Height cannot be 'auto' else skeleton doesn't show up
                  height={(props.height && props.height !== 'auto') ? props.height : '100%'}
                  style={
                    ({ position: 'relative', backgroundColor: colours.LightGray4,
                      ...((props.style && props.style.borderTopLeftRadius) ? { borderTopLeftRadius: props.style.borderTopLeftRadius } : {}),
                      ...((props.style && props.style.borderTopRightRadius) ? { borderTopRightRadius: props.style.borderTopRightRadius } : {}),
                      ...((props.style && props.style.borderBottomLeftRadius) ? { borderBottomLeftRadius: props.style.borderBottomLeftRadius } : {}),
                      ...((props.style && props.style.borderBottomRightRadius) ? { borderBottomRightRadius: props.style.borderBottomRightRadius } : {}),
                      ...((props.style && props.style.borderRadius) ? { borderRadius: props.style.borderRadius } : {}),
                      ...((props.style && props.style.top) ? { top: props.style.top } : {}),
                      ...((props.style && props.style.left) ? { left: props.style.left } : {}),
                      ...((props.style && props.style.bottom) ? { bottom: props.style.bottom } : {}),
                      ...((props.style && props.style.right) ? { right: props.style.right } : {}),
                    })
                  }
                />
              )
            )
        }
      </div>
    </div>
  );
});

ProgressiveImage.displayName = 'ProgressiveImage';

ProgressiveImage.propTypes = {
  style:            PropTypes.objectOf(PropTypes.any),
  alt:              PropTypes.string,
  image:            PropTypes.string.isRequired,
  width:            PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height:           PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  draggable:        PropTypes.bool,
  onLoad:           PropTypes.func,
  onClick:          PropTypes.func,
  LoadingComponent: PropTypes.func,
};

ProgressiveImage.defaultProps = {
  style:            {},
  alt:              'Image',
  width:            '100%',
  height:           '200px',
  onLoad:           () => {},
  onClick:          () => {},
  draggable:        true,
  LoadingComponent: null,
};

export default ProgressiveImage;
